import { Component } from "react";

/** A dynamic import() failure (`lazy()` chunk fetch) throws a distinctly-worded error —
 * webpack's ChunkLoadError in dev, "Failed to fetch dynamically imported module"/"error
 * loading dynamically imported module" in most browsers after a fresh deploy ships new chunk
 * hashes while this tab still has the OLD index.html cached. A plain reload re-fetches the
 * current index.html and its real chunk map, which actually fixes it — unlike the generic
 * "this record has a bad value" message below, which would be misleading here. */
function isChunkLoadError(error) {
  const text = `${error?.name || ""} ${error?.message || ""}`;
  return /ChunkLoadError|Loading chunk|Failed to fetch dynamically imported module|error loading dynamically imported module/i.test(text);
}

/** Production bug fix (2026-10) — a single bad record (e.g. a field rendered with the wrong
 * shape) throwing during render used to unmount the ENTIRE app (React's default for an
 * uncaught render error), taking the sidebar/shell down with it — a totally blank white page
 * with no way back except typing a different URL. This catches any render error thrown by the
 * routed page (AppShell's <Outlet/>) and shows a friendly recovery screen instead, while the
 * rest of the shell (sidebar, header) stays mounted and usable. Keyed by the route's pathname
 * in AppShell.jsx (and the other 4 portal shells — Part 6 hardening) so navigating to a
 * different page after a crash remounts cleanly.
 *
 * Part 6 hardening (2026-10): also mounted ONCE at the very top of App.js, outside every
 * lazy-loaded shell/Suspense boundary — a last line of defense for an error thrown by
 * routing/auth glue itself, not just a routed page's own content. Only render errors are ever
 * caught here (React's error-boundary contract) — a 401 from an API call is handled by
 * AuthContext/Protected's own redirect logic entirely separately and is never a thrown render
 * error, so this never intercepts or masks an auth redirect. */
export class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error("RouteErrorBoundary caught a render error:", error, info);
  }

  render() {
    // Part 8 Cursor review item 7 (2026-10): every portal shell was mounting this with its
    // defaults, so a crash inside the Client Portal/Agency Portal/Partner Portal/Freelancer
    // Portal all showed the SAME "Back to Projects" link pointing at /projects — a page none
    // of those users can even reach (staff-only). `homePath`/`homeLabel` let each shell pass
    // its own context-aware home; defaults are unchanged (AppShell's staff usage needs no
    // update) and both existing fallback screens below just read these props instead of the
    // hardcoded string.
    const { homePath = "/projects", homeLabel = "Back to Projects" } = this.props;
    if (this.state.hasError) {
      if (this.state.isChunkError) {
        return (
          <div className="p-8" data-testid="route-error-boundary-chunk">
            <div className="rounded-lg border border-[#FFC817]/30 bg-[#FFC817]/5 p-8 text-center max-w-md mx-auto">
              <div className="text-sm font-semibold text-[#1C1C1E]">A new version of this app is available.</div>
              <div className="text-xs text-[#9BA1A4] mt-1.5">Reload to pick up the latest update.</div>
              <button
                onClick={() => window.location.reload()}
                data-testid="route-error-boundary-chunk-reload"
                className="mt-4 rounded-md bg-[#111213] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2A2B2D]"
              >
                Reload
              </button>
            </div>
          </div>
        );
      }
      return (
        <div className="p-8" data-testid="route-error-boundary">
          <div className="rounded-lg border border-red-500/20 bg-red-500/5 p-8 text-center max-w-md mx-auto">
            <div className="text-sm font-semibold text-[#1C1C1E]">Something went wrong loading this page.</div>
            <div className="text-xs text-[#9BA1A4] mt-1.5">This record may have an unexpected value we haven't handled yet.</div>
            <div className="flex items-center justify-center gap-2 mt-4">
              <button
                onClick={() => window.location.reload()}
                data-testid="route-error-boundary-reload"
                className="rounded-md border border-[#E7E5E4] bg-white px-3 py-1.5 text-xs font-semibold text-[#57534E] hover:border-[#FFC817]"
              >
                Reload
              </button>
              <a
                href={homePath}
                data-testid="route-error-boundary-back"
                className="rounded-md bg-[#111213] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2A2B2D]"
              >
                {homeLabel}
              </a>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
